// Mock data for OpticalDT website

export const mockData = {
  hero: {
    tagline: "Strategic Partnership with Intel",
    title: "Revolutionizing High-Performance Computing with Breakthrough Optical Processors",
    subtitle: "OpticalDT's patent-protected optical processor technology enables ultra-fast, energy-efficient computing that surpasses traditional electronic and photonic approaches - perfectly positioned for Intel collaboration.",
    stats: [
      { value: "1000x", label: "Faster Processing" },
      { value: "<0.01%", label: "Energy Consumption" },
      { value: "$1T+", label: "Market Opportunity" }
    ]
  },
  
  aboutUs: {
    title: "Who We Are",
    subtitle: "Pioneering Deep-Tech Startup Revolutionizing Computing",
    content: "OpticalDT is a breakthrough deep-tech startup developing revolutionary optical processor technology based on direct light-to-light control. Our mission is to enable ultra-fast, energy-efficient computing that overcomes the physical limits of traditional electronic systems.",
    highlights: [
      "Patent-protected innovation with proven proof of concept",
      "Compatible with existing silicon photonics manufacturing",
      "Paradigm-shifting technology for next-generation computing",
      "Strong industry relationships with Nokia and Ericsson"
    ]
  },
  
  technology: {
    title: "Our Technology",
    subtitle: "Direct Light-to-Light Control for Unprecedented Performance",
    description: "Our unique optical processor technology leverages direct light-to-light control, enabling processing speeds limited only by the speed of light while consuming drastically less energy than traditional processors.",
    
    ttlCompatibility: {
      title: "TTL Logic Compatibility - Seamless Intel Integration",
      subtitle: "OpticalDT's Revolutionary Advantage for Intel Partnership",
      description: "OpticalDT's optical processor technology fully supports TTL logic (Transistor-Transistor Logic) — the standard digital logic widely used in electronics and specifically in Intel's architecture. This compatibility ensures seamless integration with all existing Intel developments.",
      benefits: [
        {
          title: "Direct TTL Support",
          description: "Works with familiar logic levels and signals, simplifying integration with existing Intel systems",
          icon: "Cpu"
        },
        {
          title: "Minimal Architecture Changes",
          description: "Existing Intel designs can be converted to optical processors without major redesigns",
          icon: "Settings"
        },
        {
          title: "Development Tools Compatibility",
          description: "All existing Intel development and testing tools remain fully usable",
          icon: "Tool"
        },
        {
          title: "Risk Reduction",
          description: "Smooth integration path significantly reduces risk and accelerates commercialization",
          icon: "Shield"
        }
      ],
      keyAdvantages: [
        "All existing Intel developments and designs can be easily converted",
        "No major redesigns required for Intel's architecture and software",
        "Familiar logic levels ensure seamless system integration",
        "Existing development workflows remain unchanged",
        "Accelerated time-to-market for optical computing solutions"
      ]
    },
    
    features: [
      {
        title: "Light-Speed Processing",
        description: "Process data at the speed of light with no electronic bottlenecks",
        benefits: ["No electrical switching delays", "Instantaneous parallel operations", "Processing limited only by light speed"]
      },
      {
        title: "Ultra-Low Energy",
        description: "Consume <0.01% of energy compared to electronic processors",
        benefits: ["Massive energy savings", "Minimal heat generation", "Sustainable computing solution"]
      },
      {
        title: "Silicon Photonics Compatible",
        description: "Seamlessly integrate with existing manufacturing infrastructure",
        benefits: ["Scalable production", "Cost-effective manufacturing", "Industry-standard compatibility"]
      }
    ]
  },
  
  valueProposition: {
    title: "OpticalDT: The Critical Solution for Intel & U.S. Government",
    subtitle: "Revolutionary Technology That Overcomes Current Limitations & Secures Future Leadership",
    description: "When traditional computing hits physical limits, OpticalDT provides the breakthrough solution that Intel and the U.S. Government need to maintain technological supremacy and national security.",
    
    intel: {
      title: "OpticalDT: Intel's Technological Lifeline",
      description: "When Moore's Law reaches its limits and competition intensifies, OpticalDT provides the revolutionary breakthrough that Intel needs to regain market leadership and overcome current technological constraints.",
      benefits: [
        {
          title: "Technological Leadership Recovery",
          description: "Regain market dominance by integrating breakthrough optical components into Intel's product roadmap",
          impact: "Market leadership restoration"
        },
        {
          title: "New Market Access",
          description: "Enter rapidly growing AI, cloud computing, telecommunications, and defense sectors with optical solutions",
          impact: "$500B+ addressable market"
        },
        {
          title: "Revolutionary Power Efficiency",
          description: "Reduce power consumption and heat generation by 99%+, enabling compact, efficient data centers",
          impact: "Transformative cost savings"
        },
        {
          title: "Strengthened IP Portfolio",
          description: "Exclusive access to OpticalDT's patent-protected technology enhances competitive advantage",
          impact: "Sustainable market differentiation"
        },
        {
          title: "Manufacturing Synergy",
          description: "Leverage existing Intel fabs with silicon photonics compatibility for rapid scaling",
          impact: "Cost-effective production at scale"
        }
      ]
    },
    
    government: {
      title: "OpticalDT: America's Strategic Security Asset",
      description: "In an era of increasing technological competition and security threats, OpticalDT's optical computing provides the critical advantage America needs to maintain national security leadership and technological sovereignty.",
      opportunities: [
        {
          title: "Advanced Defense Procurement",
          description: "New contracts for energy-efficient, secure computing hardware tailored to defense and intelligence needs",
          agencies: ["DoD", "NSA", "CIA", "DHS"],
          impact: "Enhanced national security capabilities"
        },
        {
          title: "Critical Infrastructure Protection",
          description: "Resilient communication networks with optical security features resistant to EMP and cyber threats",
          applications: ["Power grids", "Financial systems", "Emergency communications"],
          impact: "Infrastructure resilience & security"
        },
        {
          title: "Technological Sovereignty",
          description: "Domestic manufacturing and innovation leadership through Intel-OpticalDT partnership",
          benefits: ["Reduced foreign dependency", "Secure supply chains", "Innovation leadership"],
          impact: "Strategic independence"
        },
        {
          title: "Research & Development Partnerships",
          description: "Joint programs funded by government agencies to advance optical computing research",
          funding: ["DARPA initiatives", "DoD contracts", "NIST partnerships", "NSF grants"],
          impact: "Accelerated innovation timeline"
        },
        {
          title: "Economic & Strategic Advantages",
          description: "Create high-tech jobs, maintain technological edge, and secure competitive advantages",
          outcomes: ["Job creation", "Economic growth", "Global competitiveness"],
          impact: "Long-term strategic positioning"
        }
      ]
    },
    
    callToAction: {
      title: "Strategic Imperative for U.S. Technological Superiority",
      description: "The OpticalDT-Intel collaboration represents a critical opportunity to maintain American leadership in next-generation computing and secure national interests.",
      urgency: "Time-sensitive opportunity to lead the optical computing revolution before international competitors",
      invitations: [
        "Joint Development Programs with Intel R&D Teams",
        "Government-Funded Pilot Projects and Research Initiatives", 
        "Strategic Investment and Partnership Opportunities",
        "Defense and Intelligence Agency Collaboration Programs"
      ]
    }
  },
  
  partnership: {
    title: "Why Partner with OpticalDT",
    subtitle: "Unique Advantages for Transformative Collaboration",
    advantages: [
      {
        title: "Patented Technology",
        description: "Patent-protected optical processor technology with proven proof of concept",
        icon: "Shield"
      },
      {
        title: "Working Prototypes",
        description: "Demonstrated functionality with measurable performance improvements",
        icon: "Cpu"
      },
      {
        title: "Industry Relationships",
        description: "Strong connections with Nokia, Ericsson, and other tech leaders",
        icon: "Network"
      },
      {
        title: "Market Potential",
        description: "$1+ trillion market opportunity in high-performance computing",
        icon: "TrendingUp"
      }
    ]
  },
  
  roadmap: {
    title: "Collaboration Roadmap with OpticalDT",
    subtitle: "Strategic Phases for Successful Partnership",
    phases: [
      {
        id: 1,
        title: "Initial Engagement & Technical Validation",
        timeline: "0-6 months",
        description: "Joint technical workshops and knowledge exchange",
        activities: [
          "Joint technical workshops and knowledge exchange",
          "Demonstration of OpticalDT PoC to Intel's R&D teams",
          "Alignment on integration feasibility and manufacturing compatibility"
        ],
        outcome: "Intel gains confidence in technology viability and strategic fit",
        status: "ready"
      },
      {
        id: 2,
        title: "Pilot Projects & Prototype Integration",
        timeline: "6-12 months",
        description: "Development of integrated prototypes",
        activities: [
          "Development of integrated prototypes combining OpticalDT processors with Intel platforms",
          "Pilot deployments in controlled environments (AI workloads, data centers)",
          "Performance benchmarking against existing solutions"
        ],
        outcome: "Proof of superior performance and energy efficiency in real-world scenarios",
        status: "upcoming"
      },
      {
        id: 3,
        title: "Co-Development & Manufacturing Scale-Up",
        timeline: "12-24 months",
        description: "Joint development of scalable manufacturing processes",
        activities: [
          "Joint development of scalable manufacturing processes leveraging Intel fabs",
          "Optimization of device design for mass production",
          "Expansion of pilot projects to strategic customers and government agencies"
        ],
        outcome: "Ready-to-market products with Intel's manufacturing and distribution support",
        status: "future"
      },
      {
        id: 4,
        title: "Commercial Launch & Market Expansion",
        timeline: "24+ months",
        description: "Full commercial release and market expansion",
        activities: [
          "Full commercial release of optical processor-based products",
          "Strategic marketing and sales collaboration",
          "Expansion into new markets including defense, telecom, and cloud computing"
        ],
        outcome: "Intel regains leadership in next-gen computing, OpticalDT scales globally",
        status: "future"
      }
    ]
  },
  
  contact: {
    title: "Ready to Transform Computing Together?",
    subtitle: "Partner with OpticalDT for Strategic Innovation",
    description: "Join Intel, U.S. government agencies, and strategic investors in revolutionizing high-performance computing through optical processor technology.",
    email: "bombela@softdab.tech",
    whatsapp: "+380634262680",
    callToActions: [
      "Strategic Partnership Discussions",
      "Technical Demonstrations",
      "Investment Opportunities",
      "Government Pilot Programs"
    ]
  }
};

export default mockData;